import { graphql } from 'gatsby'
import React, { useState } from 'react'
import { Helmet } from 'react-helmet'
import styled from 'styled-components'
import FamilyMemberSelect from '../../components/FamilymemberSelect'
import LeaderboardTable from '../../components/LeaderboardTable/LeaderboardTable'
import SelectDropdown from '../../components/SelectDropdown/SelectDropdown'
import {
  StyledTab,
  StyledTabList,
  StyledTabPanel,
  StyledTabs,
} from '../../components/Tabs/Tabs.styled'
import Toggle from '../../components/Toggle/Toggle'
import { LeaderboardType } from '../../containers/Leaderboards/interfaces'
import useAuth from '../../contexts/AuthContext/useAuth'
import {
  BoardItem,
  LeaderboardPointType,
  LeaderboardTeamPointType,
  useGetPlayerLeaderboardQuery,
  useGetTeamLeaderboardQuery,
} from '../../generated/api'
import { LeaderboardItemSkeleton } from './item-skeleton'
import { useSubTeams } from './use-subteams'
import { useTeamV2 } from './use-team'

enum Tab {
  Player = 'player',
  Team = 'team',
}

const TAB_LABELS: Record<Tab, string> = {
  [Tab.Player]: 'Members',
  [Tab.Team]: 'Year groups',
}

const Wrapper = styled.div`
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 1.5rem;
`

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  text-align: center;
  font-weight: bold;
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
`

const ToggleWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-bottom: 1.5rem;
`

const FilterContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
`

const FamilyMemberSelectWrapper = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: center;
`

export default function TeamLeaderboardTemplate() {
  const { user, family } = useAuth()
  const hasTeam = user?.info.teamId
  const gameId = user?.info.gameId
  const hasFamily = (family?.length || 0) > 1

  const { team, subTeam, hasSubTeams } = useTeamV2()
  const teamId = team?.teamId

  const subTeams = useSubTeams({ parentTeamId: teamId })

  const [activeTab, setActiveTab] = useState<Tab>(Tab.Player)
  const [useGems, setUseGems] = useState<boolean>(false)
  const [useAverage, setUseAverage] = useState<boolean>(false)

  const [filterSubTeam, setFilterSubTeam] = useState<string>()
  const teamIdValue = filterSubTeam || teamId

  const playerLeaderboardQuery = useGetPlayerLeaderboardQuery(
    { teamId: teamIdValue || '', gems: useGems },
    { enabled: !!teamIdValue }
  )

  const teamLeaderboardQuery = useGetTeamLeaderboardQuery(
    { gameId: gameId || '', teamId: teamId || '' },
    { enabled: !!gameId && !!teamId }
  )

  const playerBoardData =
    playerLeaderboardQuery.data?.getPlayerLeaderboardV2?.board || []
  const teamBoardData =
    teamLeaderboardQuery.data?.getTeamLeaderboardV2?.board || []
  const teamAverageBoardData =
    teamLeaderboardQuery.data?.getTeamLeaderboardV2?.averageBoard || []

  const tabs = hasSubTeams ? [Tab.Player, Tab.Team] : [Tab.Player]

  const transformLeaderboardData = (data: Partial<BoardItem>[]) => {
    return data.map((item, index) => ({
      id: item.id,
      name: item.name,
      position: index + 1,
      amount: item.points,
    }))
  }

  if (!hasTeam) {
    return (
      <Wrapper>
        <TitleRow>
          <Title>You are not part of a team!</Title>
        </TitleRow>
      </Wrapper>
    )
  }

  return (
    <>
      <Helmet>
        <title>{team?.name || 'Team'} Leaderboard</title>
      </Helmet>
      <Wrapper>
        {hasFamily && (
          <FamilyMemberSelectWrapper>
            <FamilyMemberSelect showAddMemberButton={false} />
          </FamilyMemberSelectWrapper>
        )}
        <TitleRow>
          <Title>{team?.name || 'Team'}</Title>
        </TitleRow>

        <StyledTabs>
          <StyledTabList>
            {tabs.map((tab) => (
              <StyledTab
                key={tab}
                selected={activeTab === tab}
                onClick={() => setActiveTab(tab)}
              >
                {TAB_LABELS[tab]}
              </StyledTab>
            ))}
          </StyledTabList>

          {activeTab === Tab.Player && hasSubTeams && (
            <FilterContainer>
              <SelectDropdown
                value={filterSubTeam || 'all'}
                onChange={(event) => {
                  const value = event.target.value
                  setFilterSubTeam(value === 'all' ? undefined : value)
                }}
                options={[
                  { value: 'all', display: 'All year groups' },
                  ...(subTeams || []).map(({ id, label }) => ({
                    value: id,
                    display: label,
                  })),
                ]}
                secondary
              />
            </FilterContainer>
          )}

          <StyledTabPanel>
            {activeTab === Tab.Player && (
              <>
                <ToggleWrap>
                  <Toggle
                    value={useGems ? 'gems' : 'points'}
                    onChange={(value: string) => setUseGems(value === 'gems')}
                    left={{
                      value: 'points',
                      text: 'Beat boxes',
                    }}
                    right={{
                      value: 'gems',
                      text: 'Gems',
                    }}
                  />
                </ToggleWrap>
                {playerLeaderboardQuery.isLoading ||
                playerLeaderboardQuery.isFetching ? (
                  <LoadingContainer>
                    {[...Array(5)].map((_, index) => (
                      <LeaderboardItemSkeleton key={index} />
                    ))}
                  </LoadingContainer>
                ) : (
                  <LeaderboardTable
                    data={transformLeaderboardData(playerBoardData)}
                    pointsType={
                      useGems
                        ? LeaderboardPointType.Gem
                        : LeaderboardPointType.Swipe
                    }
                    type={LeaderboardType.player}
                    total={playerBoardData.length}
                    topIndex={0}
                    bottomIndex={playerBoardData.length}
                    highlightPosition={playerBoardData.findIndex(
                      (item) => item.id === user?.id
                    )}
                  />
                )}
              </>
            )}
          </StyledTabPanel>

          <StyledTabPanel>
            {activeTab === Tab.Team && (
              <>
                <ToggleWrap>
                  <Toggle
                    value={useAverage ? 'average' : 'total'}
                    onChange={(value: string) =>
                      setUseAverage(value === 'average')
                    }
                    left={{
                      value: 'total',
                      text: 'Total',
                    }}
                    right={{
                      value: 'average',
                      text: 'Average',
                    }}
                  />
                </ToggleWrap>
                {teamLeaderboardQuery.isLoading ? (
                  <LoadingContainer>
                    {[...Array(5)].map((_, index) => (
                      <LeaderboardItemSkeleton key={index} />
                    ))}
                  </LoadingContainer>
                ) : (
                  <LeaderboardTable
                    data={transformLeaderboardData(
                      useAverage ? teamAverageBoardData : teamBoardData
                    )}
                    pointsType={
                      useAverage
                        ? LeaderboardTeamPointType.Average
                        : LeaderboardTeamPointType.All
                    }
                    type={LeaderboardType.team}
                    total={teamBoardData.length}
                    highlightPosition={(useAverage
                      ? teamAverageBoardData
                      : teamBoardData
                    ).findIndex((item) => item.id === subTeam?.teamId)}
                    topIndex={0}
                    bottomIndex={teamBoardData.length}
                  />
                )}
              </>
            )}
          </StyledTabPanel>
        </StyledTabs>
      </Wrapper>
    </>
  )
}

export const query = graphql`
  query LeaderboardsV2($language: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      ...FLocales
    }
  }
`
